import { cn } from '../../utils'
import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
    createContext,
    useContext,
    type ComponentPropsWithoutRef,
    type ReactNode,
} from 'react'

/** Column id of the drag handle column added by `Table` when `onReorder` is set. */
export const DRAG_HANDLE_COLUMN_ID = 'drag-handle'

type SortableHandle = Pick<
    ReturnType<typeof useSortable>,
    'attributes' | 'listeners' | 'setActivatorNodeRef'
>

const SortableHandleContext = createContext<SortableHandle | null>(null)

/** Wraps the table so its rows can be dragged into a new order. */
export function SortableRowsProvider({
    ids,
    onReorder,
    children,
}: {
    ids: string[]
    onReorder: (orderedIds: string[]) => void
    children: ReactNode
}) {
    const sensors = useSensors(
        // A small distance keeps a click on the handle from starting a drag.
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    function handleDragEnd({ active, over }: DragEndEvent) {
        if (!over || active.id === over.id) return
        const from = ids.indexOf(String(active.id))
        const to = ids.indexOf(String(over.id))
        if (from < 0 || to < 0) return
        onReorder(arrayMove(ids, from, to))
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            // The provider sits inside <tbody>, where dnd-kit's screen reader
            // elements (divs) would be invalid markup.
            accessibility={{ container: document.body }}
        >
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                {children}
            </SortableContext>
        </DndContext>
    )
}

/** `SortableRowsProvider` when `onReorder` is set, otherwise just the children. */
export function OptionalSortableRows({
    ids,
    onReorder,
    children,
}: {
    ids: string[]
    onReorder?: (orderedIds: string[]) => void
    children: ReactNode
}) {
    if (!onReorder) return <>{children}</>
    return (
        <SortableRowsProvider ids={ids} onReorder={onReorder}>
            {children}
        </SortableRowsProvider>
    )
}

/** Header and cells of the select and drag handle columns share one layout. */
export function isControlColumn(columnId: string) {
    return columnId === 'select' || columnId === DRAG_HANDLE_COLUMN_ID
}

/** A `<tr>` that moves while dragged; its handle cell is `RowDragHandle`. */
export function SortableTableRow({
    id,
    style,
    ...props
}: { id: string } & ComponentPropsWithoutRef<'tr'>) {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id })

    return (
        <SortableHandleContext.Provider
            value={{ attributes, listeners, setActivatorNodeRef }}
        >
            <tr
                ref={setNodeRef}
                style={{
                    ...style,
                    transform: CSS.Translate.toString(transform),
                    transition,
                    position: 'relative',
                    zIndex: isDragging ? 1 : undefined,
                    opacity: isDragging ? 0.8 : undefined,
                }}
                {...props}
            />
        </SortableHandleContext.Provider>
    )
}

/** Plain `<tr>` with the same props as `SortableTableRow`. */
export function StaticTableRow({
    id: _id,
    ...props
}: { id: string } & ComponentPropsWithoutRef<'tr'>) {
    return <tr {...props} />
}

/** Six-dot drag handle; renders nothing outside a `SortableTableRow`. */
export function RowDragHandle({ label }: { label: string }) {
    const handle = useContext(SortableHandleContext)
    if (!handle) return null
    return (
        <button
            type="button"
            ref={handle.setActivatorNodeRef}
            aria-label={label}
            title={label}
            className={cn(
                'flex touch-none items-center justify-center rounded p-1',
                'cursor-grab text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-title))] active:cursor-grabbing',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500'
            )}
            {...handle.attributes}
            {...handle.listeners}
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width={16}
                height={16}
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
            >
                <circle cx={9} cy={6} r={1.5} />
                <circle cx={9} cy={12} r={1.5} />
                <circle cx={9} cy={18} r={1.5} />
                <circle cx={15} cy={6} r={1.5} />
                <circle cx={15} cy={12} r={1.5} />
                <circle cx={15} cy={18} r={1.5} />
            </svg>
        </button>
    )
}
